import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Reveal, Eyebrow } from "../components/Reveal";
import { PageHero, FinalCta } from "../components/Sections";
import { ARTICLES, articleBySlug } from "../data/articles";

export const Insights = () => {
    const [filter, setFilter] = useState("All");
    const categories = ["All", ...new Set(ARTICLES.map((a) => a.category))];
    const visible = filter === "All" ? ARTICLES : ARTICLES.filter((a) => a.category === filter);

    useEffect(() => {
        document.title = "Insights | Marketing & Sales Growth Articles — ALBL Narmadapuram";
    }, []);

    return (
        <main>
            <PageHero
                eyebrow="Insights"
                title="THINKING THAT GROWS BUSINESSES."
                sub="Practical strategy notes on brand, marketing and sales growth for businesses in Narmadapuram, Itarsi and surrounding markets."
            />
            <section className="bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-20 sm:py-28">
                    <Reveal>
                        <div className="flex flex-wrap gap-3" data-testid="insights-category-filter">
                            {categories.map((c) => (
                                <button
                                    key={c}
                                    onClick={() => setFilter(c)}
                                    data-testid={`insights-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                                    className={`btn-sharp px-5 py-2.5 text-xs font-bold tracking-[0.12em] uppercase border ${
                                        filter === c
                                            ? "bg-[#0D131F] text-white border-[#0D131F]"
                                            : "border-slate-300 text-slate-600 hover:border-[#0D131F]"
                                    }`}
                                >
                                    {c}
                                </button>
                            ))}
                        </div>
                    </Reveal>
                    <div className="mt-12 grid gap-px bg-slate-200 border border-slate-200">
                        {visible.map((a, i) => (
                            <Reveal key={a.slug} delay={i * 60}>
                                <Link
                                    to={`/insights/${a.slug}`}
                                    data-testid={`article-card-${a.slug}`}
                                    className="group grid md:grid-cols-12 gap-4 md:gap-8 items-baseline bg-white hover:bg-[#F8FAFC] transition-colors px-6 sm:px-10 py-8"
                                >
                                    <div className="md:col-span-3">
                                        <span className="text-xs tracking-[0.2em] uppercase font-semibold text-[#C5A059]">
                                            {a.category}
                                        </span>
                                        <p className="text-xs text-slate-400 mt-2">
                                            {new Date(a.date).toLocaleDateString("en-IN", {
                                                day: "numeric",
                                                month: "long",
                                                year: "numeric",
                                            })}{" "}
                                            · {a.readTime}
                                        </p>
                                    </div>
                                    <div className="md:col-span-8">
                                        <h2 className="font-display text-xl sm:text-2xl font-bold text-slate-900 tracking-tight group-hover:text-[#1E3A8A] transition-colors">
                                            {a.title}
                                        </h2>
                                        <p className="mt-3 text-sm sm:text-base text-slate-600 leading-relaxed">
                                            {a.excerpt}
                                        </p>
                                    </div>
                                    <ArrowUpRight className="hidden md:block h-5 w-5 text-slate-300 group-hover:text-[#C5A059] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all md:col-span-1 justify-self-end" />
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>
            <FinalCta />
        </main>
    );
};

export const Article = ({ slug }) => {
    const article = articleBySlug(slug);

    useEffect(() => {
        if (article) {
            document.title = `${article.title} | ALBL Communications`;
            const script = document.createElement("script");
            script.type = "application/ld+json";
            script.id = "article-schema";
            script.text = JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Article",
                headline: article.title,
                description: article.meta,
                datePublished: article.date,
                author: {
                    "@type": "Organization",
                    name: "ALBL Communications Private Limited",
                },
                publisher: {
                    "@type": "Organization",
                    name: "ALBL Communications Private Limited",
                },
            });
            document.head.appendChild(script);
            return () => document.getElementById("article-schema")?.remove();
        }
    }, [article]);

    if (!article) {
        return (
            <main className="pt-40 pb-24 text-center">
                <h1 className="font-display text-3xl font-bold text-slate-900">Article not found.</h1>
                <Link to="/insights" className="mt-6 inline-block text-[#1E3A8A] text-sm font-semibold" data-testid="article-not-found-back">
                    Back to Insights
                </Link>
            </main>
        );
    }

    const idx = ARTICLES.findIndex((a) => a.slug === slug);
    const next = ARTICLES[(idx + 1) % ARTICLES.length];

    return (
        <main>
            <section className="bg-[#0D131F] hero-grid text-white pt-32 sm:pt-40 pb-16 sm:pb-24">
                <div className="max-w-3xl mx-auto px-4 sm:px-6">
                    <Reveal>
                        <span className="text-xs tracking-[0.25em] uppercase font-semibold text-[#C5A059]">
                            {article.category}
                        </span>
                        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.12] mt-5">
                            {article.title}
                        </h1>
                        <p className="mt-5 text-sm text-slate-400">
                            {new Date(article.date).toLocaleDateString("en-IN", {
                                day: "numeric",
                                month: "long",
                                year: "numeric",
                            })}{" "}
                            · {article.readTime} · ALBL Communications
                        </p>
                    </Reveal>
                </div>
            </section>
            <section className="bg-white">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
                    <article data-testid="article-body">
                        {article.content.map((block, i) => {
                            if (block.type === "h2")
                                return (
                                    <h2
                                        key={i}
                                        className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mt-12 mb-4"
                                    >
                                        {block.text}
                                    </h2>
                                );
                            if (block.type === "cta")
                                return (
                                    <div key={i} className="my-10 border-l-2 border-[#C5A059] bg-[#F8FAFC] px-6 py-6">
                                        <p className="text-slate-800 text-base sm:text-lg font-medium leading-relaxed">
                                            {block.text}
                                        </p>
                                        <Link
                                            to="/contact?intent=audit"
                                            data-testid="article-inline-cta"
                                            className="btn-sharp mt-4 inline-flex items-center gap-2 text-xs font-bold tracking-[0.15em] uppercase text-[#0D131F]"
                                        >
                                            Get a Growth Audit <ArrowRight className="h-4 w-4 text-[#C5A059]" />
                                        </Link>
                                    </div>
                                );
                            if (block.type === "list")
                                return (
                                    <ul key={i} className="my-6 space-y-4">
                                        {block.items.map((item) => (
                                            <li key={item} className="flex gap-3 items-start">
                                                <span className="mt-2.5 h-1.5 w-1.5 bg-[#C5A059] shrink-0" />
                                                <span className="text-slate-700 text-base leading-relaxed">{item}</span>
                                            </li>
                                        ))}
                                    </ul>
                                );
                            return (
                                <p key={i} className="text-slate-700 text-base sm:text-lg leading-relaxed my-6">
                                    {block.text}
                                </p>
                            );
                        })}
                    </article>
                    <div className="mt-16 border border-slate-200 bg-[#F8FAFC] p-8 sm:p-10">
                        <Eyebrow>Work with ALBL</Eyebrow>
                        <p className="mt-4 font-display text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
                            Want this thinking applied to your business?
                        </p>
                        <Link
                            to="/contact?intent=audit"
                            data-testid="article-cta-audit-button"
                            className="btn-sharp mt-6 inline-flex items-center gap-2 bg-[#0D131F] text-white text-xs font-bold tracking-[0.15em] uppercase px-8 py-4"
                        >
                            Get a Growth Audit <ArrowRight className="h-4 w-4 text-[#C5A059]" />
                        </Link>
                    </div>
                    <Link
                        to={`/insights/${next.slug}`}
                        data-testid="article-next-link"
                        className="group mt-12 flex items-center justify-between border-t border-slate-200 pt-8"
                    >
                        <div>
                            <p className="text-xs tracking-[0.25em] uppercase font-semibold text-slate-400">
                                Next article
                            </p>
                            <p className="mt-2 font-display text-lg font-bold text-slate-900 group-hover:text-[#1E3A8A] transition-colors">
                                {next.title}
                            </p>
                        </div>
                        <ArrowRight className="h-5 w-5 text-slate-400 group-hover:text-[#C5A059] group-hover:translate-x-1 transition-all" />
                    </Link>
                </div>
            </section>
            <FinalCta />
        </main>
    );
};

export default Insights;
